import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Grid3x3, Calendar, MapPin, Link, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const WidgetEmbedsSection = ({ organizationId }) => {
  const [selectedWidget, setSelectedWidget] = useState('service-grid');
  const [embedToken, setEmbedToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [tokenValid, setTokenValid] = useState(null); // null = not checked, true = valid, false = invalid
  
  // Use REACT_APP_BACKEND_URL for embeds (configured per environment)
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const orgName = localStorage.getItem('organizationName') || 'BOATX';
  
  console.log('WidgetEmbedsSection - organizationId:', organizationId);
  console.log('WidgetEmbedsSection - baseUrl:', baseUrl);
  console.log('WidgetEmbedsSection - embedToken:', embedToken);
  
  const validateToken = async (token) => {
    try {
      // Test the token by calling the public services endpoint
      const response = await fetch(`${baseUrl}/api/public/services?token=${token}`);
      
      if (response.ok) {
        const data = await response.json();
        // Check if we got services back
        if (data.services !== undefined) {
          setTokenValid(true);
          return true;
        }
      }
      
      setTokenValid(false);
      return false;
    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
      return false;
    }
  };
  
  // Fetch current token on mount - NEVER auto-generate
  useEffect(() => {
    if (organizationId && !embedToken) {
      fetchCurrentToken();
    }
  }, [organizationId]);
  
  const fetchCurrentToken = async () => {
    try {
      const orgSlug = localStorage.getItem('organizationSlug');
      
      if (!orgSlug) {
        console.warn('No org slug found');
        return;
      }
      
      // Fetch existing token (does NOT create a new one)
      const response = await fetch(`${baseUrl}/api/embed-tokens/current/${orgSlug}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch token');
      }
      
      const data = await response.json();
      
      if (data.exists && data.token) {
        setEmbedToken(data.token);
        localStorage.setItem(`embedToken_${organizationId}`, data.token);
        console.log('Loaded existing active token');
      } else {
        console.log('No active token exists - user must generate one');
        setEmbedToken(''); // Empty state - will show "Generate" UI
      }
    } catch (error) {
      console.error('Error fetching current token:', error);
    }
  };

  
  // Validate token whenever it changes
  useEffect(() => {
    if (embedToken) {
      validateToken(embedToken);
    }
  }, [embedToken]);

  
  const generateEmbedToken = async () => {
    setLoading(true);
    try {
      // Get org slug from localStorage
      const orgSlug = localStorage.getItem('organizationSlug');
      
      if (!orgSlug) {
        console.warn('No org slug found, using fallback');
        const encoded = btoa(organizationId);
        setEmbedToken(encoded);
        toast.warning('Using legacy embed method');
        setLoading(false);
        return;
      }
      
      // Call PUBLIC endpoint (no auth needed)
      const response = await fetch(`${baseUrl}/api/embed-tokens/generate/${orgSlug}`);
      
      if (!response.ok) {
        throw new Error('Failed to generate token');
      }
      
      const data = await response.json();
      setEmbedToken(data.token);
      // Store token for reuse
      localStorage.setItem(`embedToken_${organizationId}`, data.token);
      
      // Validate token immediately
      await validateToken(data.token);
      
      toast.success('🔒 Secure embed token generated');
    } catch (error) {
      console.error('Error generating embed token:', error);
      // Fallback to base64 org ID
      const encoded = btoa(organizationId);
      setEmbedToken(encoded);
      toast.error('Failed to generate secure token, using fallback');

  

    } finally {
      setLoading(false);
    }
  };
  
  const handleRegenerateToken = () => {
    // Show first confirmation modal
    setShowRegenerateModal(true);
  };
  
  const handleFirstConfirm = () => {
    // Close first modal, show second confirmation
    setShowRegenerateModal(false);
    setConfirmText(''); // Reset input
    setShowFinalConfirm(true);
  };
  
  const handleFinalConfirm = () => {
    // Only proceed if user typed "new token"
    if (confirmText.toLowerCase() === 'new token') {
      setShowFinalConfirm(false);
      setConfirmText('');
      generateEmbedToken();
    }
  };
  
  const handleCancel = () => {
    setShowRegenerateModal(false);
    setShowFinalConfirm(false);
    setConfirmText('');
  };
  
  
  // Handle iframe resize and modal messages for preview
  useEffect(() => {
    let modalOverlay = null;
    let loadingSpinner = null;
    
    const handleMessage = (e) => {
      // Handle resize
      if (e.data.type === 'RESIZE_IFRAME') {
        const iframe = document.getElementById('preview-iframe');
        if (iframe) {
          iframe.style.height = e.data.height + 'px';
        }
      }
      
      // Handle booking modal open in preview
      if (e.data.type === 'OPEN_BOOKING_MODAL') {
        // Keep preview visible - just overlay the modal
        
        if (!modalOverlay) {
          modalOverlay = document.createElement('div');
          modalOverlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(8px); z-index: 999999; display: flex; align-items: center; justify-content: center; padding: 20px; overflow-y: auto; opacity: 0; transition: opacity 0.3s ease;';
          
          // DON'T close on background click - user must use X button
          
          // Create loading spinner
          loadingSpinner = document.createElement('div');
          loadingSpinner.style.cssText = 'text-align: center; color: white;';
          loadingSpinner.innerHTML = `
            <div style="display: inline-block; width: 48px; height: 48px; border: 4px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 1s linear infinite;"></div>
            <p style="margin-top: 16px; font-size: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Loading...</p>
            <style>@keyframes spin { to { transform: rotate(360deg); }}</style>
          `;
          modalOverlay.appendChild(loadingSpinner);
          
          const modalIframe = document.createElement('iframe');
          modalIframe.style.cssText = 'width: 100%; max-width: 850px; height: 90vh; border: none; border-radius: 20px; background: white; opacity: 0; transform: scale(0.95); transition: opacity 0.3s ease, transform 0.3s ease; display: none;';
          
          // Don't try to decode anything - just use the embedToken state directly
          // If embedToken is not available yet, skip modal creation
          if (!embedToken) {
            console.error('Cannot open modal: embedToken not ready');
            return;
          }
          
          // Check if it's a secure HMAC token or legacy base64
          let modalUrl;
          try {
            const decoded = atob(embedToken);
            if (decoded.includes(':')) {
              // Secure HMAC token - use ?token=
              modalUrl = `${baseUrl}/services-grid.html?token=${embedToken}&fullscreen=true&service=${e.data.serviceId}`;
            } else {
              // Legacy base64 org ID - use ?org=
              modalUrl = `${baseUrl}/services-grid.html?org=${embedToken}&fullscreen=true&service=${e.data.serviceId}`;
            }
          } catch (err) {
            // Can't decode, assume it's a secure token
            modalUrl = `${baseUrl}/services-grid.html?token=${embedToken}&fullscreen=true&service=${e.data.serviceId}`;
          }
          
          console.log('Opening modal with URL:', modalUrl);
          modalIframe.src = modalUrl;
          
          modalIframe.onload = function() {
            setTimeout(function() {
              if (loadingSpinner) loadingSpinner.style.display = 'none';
              modalIframe.style.display = 'block';
              setTimeout(function() {
                modalIframe.style.opacity = '1';
                modalIframe.style.transform = 'scale(1)';
              }, 50);
            }, 300);
          };
          
          modalOverlay.appendChild(modalIframe);
          document.body.appendChild(modalOverlay);
          document.body.style.overflow = 'hidden';
          
          setTimeout(function() {
            modalOverlay.style.opacity = '1';
          }, 50);
        }
      }
      
      // Handle modal close
      if (e.data.type === 'CLOSE_BOOKING_MODAL') {
        closeModal();
      }
    };
    
    const closeModal = () => {
      if (modalOverlay) {
        // INSTANT close - no fade, just *snap* gone
        modalOverlay.remove();
        modalOverlay = null;
        loadingSpinner = null;
        document.body.style.overflow = '';
        
        // Preview stays visible
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (modalOverlay) modalOverlay.remove();
    };
  }, [organizationId, baseUrl, embedToken]);
  
  // Determine if we're using secure token or legacy base64
  // Use direct org ID as fallback if embedToken is empty
  const effectiveToken = embedToken || btoa(organizationId);
  
  // Debug logging
  console.log('🔍 Embed Token Debug:', {
    embedToken: embedToken ? `${embedToken.substring(0, 20)}...` : 'EMPTY',
    effectiveToken: effectiveToken ? `${effectiveToken.substring(0, 20)}...` : 'EMPTY',
    organizationId
  });
  
  // NEW: Encrypted tokens can't be decoded, so we use a simpler check
  // If embedToken was generated (not empty), it's a secure token
  // If it's a fallback (btoa of org ID), use 'org' parameter
  let paramName = 'token';
  let paramValue = effectiveToken;
  
  if (!embedToken) {
    // No token generated yet - using fallback base64 org ID
    paramName = 'org';
    paramValue = effectiveToken;
    console.log('⚠️ Using fallback org parameter (no secure token generated)');
  } else {
    console.log('✅ Using secure encrypted token');
  }
  // Otherwise use 'token' (default for encrypted tokens)
  
  const widgets = [
    {
      id: 'service-grid',
      name: 'Services First',
      icon: Grid3x3,
      description: 'Show all services in a grid layout',
      previewUrl: `${baseUrl}/services-grid.html?${paramName}=${paramValue}&embed=true`,
      embedCode: `<script src="${baseUrl}/embeds/calendar.js?${paramName}=${paramValue}&view=services"></script>`
    },
    {
      id: 'calendar-first',
      name: 'Calendar First',
      icon: Calendar,
      description: 'Start with date picker, then show services',
      previewUrl: `${baseUrl}/booking.html?${paramName}=${paramValue}&embed=true`,
      embedCode: `<script src="${baseUrl}/embeds/calendar.js?${paramName}=${paramValue}&view=calendar"></script>`
    },
    {
      id: 'book-now-button',
      name: 'Floating Button',
      icon: MapPin,
      description: 'Sticky button that opens booking in modal',
      previewUrl: null, // No preview for button
      embedCode: `<script src="${baseUrl}/embeds/button.js?${paramName}=${paramValue}"></script>`
    },
    {
      id: 'direct-link',
      name: 'Direct Link',
      icon: Link,
      description: 'Share direct URL to your booking page',
      previewUrl: null,
      embedCode: `${baseUrl}/book?${paramName}=${paramValue}`
    }
  ];

  const selectedWidgetData = widgets.find(w => w.id === selectedWidget);

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(selectedWidgetData.embedCode);
    toast.success('Code copied to clipboard!');
  };

  // Syntax highlighter with proper colors
  const renderCodeWithColors = (code) => {
    // Direct link - just show as cyan
    if (!code.includes('<') && !code.includes('function')) {
      return <span className="text-cyan-400">{code}</span>;
    }

    // Parse the script tag manually for precise coloring
    const scriptMatch = code.match(/<script\s+src="([^"]+)"><\/script>/);
    if (scriptMatch) {
      const fullUrl = scriptMatch[1];
      const urlParts = fullUrl.split('?');
      const baseUrlPart = urlParts[0];
      const queryString = urlParts[1] || '';
      
      // Parse query params
      const tokenMatch = queryString.match(/token=([^&]+)/);
      const viewMatch = queryString.match(/view=([^&]+)/);
      
      return (
        <span>
          <span className="text-pink-500">&lt;script </span>
          <span className="text-green-400">src</span>
          <span className="text-gray-400">=</span>
          <span className="text-blue-400">"</span>
          <span className="text-blue-300">{baseUrlPart}</span>
          {tokenMatch && (
            <>
              <span className="text-purple-400">?token=</span>
              <span className="text-teal-400">{tokenMatch[1]}</span>
            </>
          )}
          {viewMatch && (
            <>
              <span className="text-purple-400">&amp;view=</span>
              <span className="text-yellow-400">{viewMatch[1]}</span>
            </>
          )}
          <span className="text-blue-400">"</span>
          <span className="text-pink-500">&gt;&lt;/script&gt;</span>
        </span>
      );
    }
    
    // Fallback for other formats
    return <span className="text-gray-300">{code}</span>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg sm:text-xl">Choose Your Flow</CardTitle>
        <p className="text-sm text-gray-600 mt-1">Select a flow type and paste one line of code into your website</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Flow Type Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {widgets.map((widget) => {
            const Icon = widget.icon;
            return (
              <button
                key={widget.id}
                onClick={() => setSelectedWidget(widget.id)}
                className={`p-4 border rounded-xl text-left transition-all hover:scale-105 ${
                  selectedWidget === widget.id
                    ? 'border-blue-600 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <Icon className="w-7 h-7 mb-2 text-gray-700" />
                <div className="font-semibold text-sm mb-1">{widget.name}</div>
                <div className="text-xs text-gray-500">{widget.description}</div>
              </button>
            );
          })}
        </div>

        {/* Embed Code - COMES FIRST */}
        {selectedWidgetData && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Embed Code</h3>
                <p className="text-xs text-gray-500 mt-0.5">Copy and paste this into your website's HTML</p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={copyEmbedCode}
                  className="text-xs"
                  disabled={!effectiveToken}
                >
                  Copy Code
                </Button>
              </div>
            </div>
            
            {embedToken ? (
              <>
                {/* Token Status - Simple Text with Icon */}
                <div className="mb-2 flex items-center gap-1.5">
                  {tokenValid === null && (
                    <>
                      <svg className="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-xs text-gray-500">Validating token...</span>
                    </>
                  )}
                  {tokenValid === true && (
                    <>
                      <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xs text-green-700 font-medium">Token Active</span>
                    </>
                  )}
                  {tokenValid === false && (
                    <>
                      <svg className="w-3.5 h-3.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-xs text-red-700 font-medium">Token Invalidated</span>
                    </>
                  )}
                </div>
                
                <div className="bg-[#1e1e1e] border border-gray-700 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <pre className="leading-relaxed">
                    {renderCodeWithColors(selectedWidgetData.embedCode)}
                  </pre>
                </div>
                
                <div className="text-xs text-gray-600 leading-relaxed flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>
                    Paste this {selectedWidget === 'direct-link' ? 'link' : 'code'} into your website. 
                    {selectedWidget !== 'direct-link' && selectedWidget !== 'book-now-button' && ' The booking flow will automatically appear and resize to fit your content. '} 
                    Widget compromised or need to revoke affiliate access? You may{' '}
                    <button 
                      onClick={handleRegenerateToken}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      regenerate a new token
                    </button>{' '}
                    to remove their access.
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Generating secure token...</p>
              </div>
            )}
          </div>
        )}

        {/* Live Preview - COMES SECOND, NO SCROLLING */}
        {selectedWidgetData && selectedWidgetData.previewUrl && (
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Live Preview</h3>
              <p className="text-xs text-gray-500 mt-0.5">This is how your booking flow will look on your website</p>
            </div>
            
            {/* Full height iframe - no restrictions */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <iframe
                src={selectedWidgetData.previewUrl}
                className="w-full"
                style={{ minHeight: '800px', border: 'none' }}
                title="Booking Flow Preview"
                id="preview-iframe"
              />
            </div>
          </div>
        )}
        
        {/* No preview message for button and link */}
        {selectedWidgetData && !selectedWidgetData.previewUrl && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <div className="text-4xl mb-3">
              {selectedWidget === 'book-now-button' ? '🔘' : '🔗'}
            </div>
            <p className="text-sm text-gray-600">
              {selectedWidget === 'book-now-button' 
                ? 'The floating button will appear in the bottom-right corner of your website'
                : 'Share this link directly with your customers via email, SMS, or social media'}
            </p>
          </div>
        )}
      </CardContent>
      
      {/* First Confirmation Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleCancel}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Regenerate Embed Token?</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  This will make any old versions of your embed code stop working. You'll need to replace the code on all websites where it's currently embedded.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFirstConfirm}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
              >
                I Understand
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Final Confirmation Modal - Requires typing "new token" */}
      {showFinalConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleCancel}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Token Regeneration</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  All existing embed codes will stop working immediately. Type <strong className="text-gray-900">new token</strong> below to confirm.
                </p>
                
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="new token"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFinalConfirm}
                disabled={confirmText.toLowerCase() !== 'new token'}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Regenerate
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default WidgetEmbedsSection;
